import { useEffect, useMemo, useRef, useState } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Plus } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import DraftCard from "@/components/tuw/DraftCard"
import DiscardDraftDialog from "@/components/tuw/DiscardDraftDialog"
import FreelancerRequestsDialog from "@/components/tuw/FreelancerRequestsDialog"
import ProjectRow from "@/components/tuw/ProjectRow"
import SearchField from "@/components/tuw/SearchField"
import SortDropdown from "@/components/tuw/hire/SortDropdown"
import { Button } from "@/components/ui/button"
import { discardDraft } from "@/lib/postProjectActions"
import { cn } from "@/lib/utils"

// S-PM-DRAFTS: the Drafts strip above the live projects table. Renders one card
// per unposted draft (most-recent first); absent entirely when there are none
// (state D2). Discard opens the shared DS confirm, then reloads only the drafts +
// navCounts props so the card drops and the strip collapses when the last one
// goes.
function DraftsStrip({ drafts }) {
  const [discarding, setDiscarding] = useState(null)

  if (!drafts.length) return null

  const confirmDiscard = (draft) => {
    discardDraft(draft.id, {
      onFinish: () => router.reload({ only: ["drafts", "navCounts"] }),
    })
    setDiscarding(null)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <h2 className="font-display text-[15px] font-bold tracking-tight">Drafts</h2>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
          {drafts.length}
        </span>
        <span className="text-[12.5px] text-ink-3">
          Unposted projects — pick up where you left off.
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        {drafts.map((draft) => (
          <DraftCard key={draft.id} draft={draft} variant="strip" onDiscard={setDiscarding} />
        ))}
      </div>

      <DiscardDraftDialog
        draft={discarding}
        onOpenChange={() => setDiscarding(null)}
        onConfirm={confirmDiscard}
      />
    </div>
  )
}

// Toolbar filter chips. Keys line up with the per-project `bucket` the
// controller assigns (active / awaiting / hold / completed); "all" shows
// everything. Counts come from props.counts.
const CHIPS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "awaiting", label: "Awaiting you" },
  { key: "hold", label: "On hold" },
  { key: "completed", label: "Completed" },
]

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "name_asc", label: "Project name A–Z" },
  { key: "name_desc", label: "Project name Z–A" },
]

const COLUMNS = ["Project", "Status", "Order date", "Package", "Progress", "Team"]

// The two columns that leave the layout from 1199 down (P8-H4 "Cardstock" —
// their <td> counterparts carry the same rule in ProjectRow).
const BAND_DROPPED = ["Order date", "Package"]

function EmptyState({ hasProjects }) {
  return (
    <div className="px-5 py-16 text-center">
      <h3 className="font-display text-[15px] font-bold">
        {hasProjects ? "No projects match" : "No active projects yet"}
      </h3>
      <p className="mx-auto mt-1 max-w-[340px] text-[12.5px] text-ink-3">
        {hasProjects
          ? "Try a different filter or clear your search."
          : "Your projects appear here once you post one."}
      </p>
    </div>
  )
}

// The topbar search lands here as /projects?q=<term> (UPDA-98): the list's own
// filter (title or order number) opens already set to it. Read once at mount
// from the page URL — a fresh visit remounts this page, so a second search
// re-seeds it.
function queryFromUrl(url) {
  try {
    return new URL(url, "http://x").searchParams.get("q")?.trim() || ""
  } catch {
    return ""
  }
}

export default function Projects({ projects = [], counts = {}, summary = {}, drafts = [], requestOrderId = null }) {
  const { url } = usePage()
  const [filter, setFilter] = useState("all")
  const [query, setQuery] = useState(() => queryFromUrl(url))
  const [sort, setSort] = useState("newest")
  const [reviewing, setReviewing] = useState(null)
  const openedDeepLink = useRef(false)

  useEffect(() => {
    if (!requestOrderId || openedDeepLink.current) return

    const rows = projects.flatMap((project) => [...(project.children || []), project])
    const match = rows.find((row) => row.requestItems?.some((request) => request.orderId === requestOrderId))
    openedDeepLink.current = true
    if (match) setReviewing(match)
  }, [projects, requestOrderId])

  // Chips + search both filter client-side over the customer's own projects
  // (a bounded set), so there are no extra round-trips while browsing.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()

    // Order numbers are searched as well as names: a customer who has the
    // number to hand shouldn't have to remember what they called the project.
    // "#12345" and "12345" are the same search; a bare "#" matches nothing.
    const qNumber = q.replace(/^#/, "")
    const rowMatches = (row) =>
      row.title.toLowerCase().includes(q) ||
      (!!qNumber && !!row.orderNumber && row.orderNumber.includes(qNumber))

    const filtered = projects.filter((project) => {
      const bucketOk = filter === "all" || project.bucket === filter
      const searchOk =
        !q || rowMatches(project) || (project.children || []).some(rowMatches)

      return bucketOk && searchOk
    })

    return filtered.sort((a, b) => {
      if (sort === "name_asc") return a.title.localeCompare(b.title)
      if (sort === "name_desc") return b.title.localeCompare(a.title)

      const aDate = Date.parse(a.sortDate || "") || 0
      const bDate = Date.parse(b.sortDate || "") || 0
      return sort === "oldest" ? aDate - bDate : bDate - aDate
    })
  }, [projects, filter, query, sort])

  const activeCount = summary.activeProjects ?? 0
  const awaitingCount = summary.awaitingAction ?? 0

  return (
    <>
      {/* P8-H4 "Cardstock": below 860 the page tightens to the design's
          16px/14px/40px frame and the table collapses to stacked cards (the row
          classes live in ProjectRow); 861–1199 keeps a real table minus the
          Order-date and Package columns. Desktop (≥1200) is untouched. */}
      <div className="flex flex-col gap-5 px-[26px] pt-6 pb-11 max-[860px]:gap-4 max-[860px]:px-3.5 max-[860px]:pt-4 max-[860px]:pb-10">
        {/* page header */}
        <div className="flex items-end justify-between max-[860px]:flex-wrap max-[860px]:gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.4px]">
              Projects
            </h1>
            <p className="mt-1 text-[13px] text-ink-3">
              {activeCount} active project{activeCount === 1 ? "" : "s"} ·{" "}
              {awaitingCount} awaiting your action
            </p>
          </div>
          <Button asChild className="gap-[7px] font-bold">
            <Link href="/post">
              <Plus className="size-4" strokeWidth={2.2} />
              Post a Project
            </Link>
          </Button>
        </div>

        {/* S-PM-DRAFTS: drafts strip (above the toolbar, matching the prototype) */}
        <DraftsStrip drafts={drafts} />

        {/* toolbar: filter chips + search */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-[7px] max-[860px]:max-w-full max-[860px]:overflow-x-auto max-[860px]:[scrollbar-width:none] max-[860px]:[&::-webkit-scrollbar]:hidden">
            {CHIPS.map((chip) => {
              const on = filter === chip.key
              const count = chip.key === "all" ? counts.all : counts[chip.key]

              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setFilter(chip.key)}
                  className={cn(
                    "flex items-center gap-[7px] whitespace-nowrap rounded-[9px] border px-[13px] py-[7px] text-[12.5px] font-semibold transition-colors",
                    on
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:bg-accent"
                  )}
                >
                  {chip.label}
                  <span
                    className={cn(
                      "text-[11px] font-bold",
                      on ? "text-primary-foreground/80" : "text-ink-3"
                    )}
                  >
                    {count ?? 0}
                  </span>
                </button>
              )
            })}
          </div>

          <SearchField
            className="ml-auto max-w-[230px] flex-1 py-2 max-[860px]:ml-0 max-[860px]:max-w-none max-[860px]:basis-full"
            value={query}
            onChange={setQuery}
            placeholder="Search projects…"
          />
          <SortDropdown value={sort} options={SORTS} onPick={setSort} />
        </div>

        {/* table — below 860 the wrapper unwraps so each row card carries its
            own border; from 860 up it keeps a horizontal pan as the safety net
            for whatever columns are in the layout.
            THE NET HAS NO UPPER BOUND, deliberately. It used to stop at
            `max-[1199px]`, and BAND_DROPPED brings its two columns back at the
            same edge (`max-[1199px]:hidden` = `width < 1199`), so at 1199 all
            eight columns returned AND the pan switched off in the same instant
            while the table still needed more width than the card: measured on a
            15-row fixture, 234px of table unreachable at 1199, 173 at 1260, 93
            at 1340, 33 at 1400, clean only from ~1433 — with `overflow-hidden`
            swallowing it, so no scrollbar and no document overflow, and every
            probe we own reported the page clean. A wider number would just rot
            (the clean width is content-dependent); `overflow-x:auto` renders
            identically to `hidden` when the content fits, so the net simply
            stays on. On the vertical axis the wrapper declares nothing, so CSS
            promotes its `visible` to `auto` next to an `overflow-x` that is not
            visible; its height is auto, so it has nothing to scroll and renders
            no vertical scrollbar (measured at 860/861/1024/1112/1199/1440:
            scrollHeight == clientHeight on every one). It also starts at 860,
            not 861: `max-[860px]` is `width < 860`, so exactly 860 fell through
            both rules and rendered the desktop table inside a hard-clipping
            card. Every breakpoint literal in THIS file is deliberately the
            naked design number, unlike OrderHeader.jsx: the pair here
            (`max-[860px]` / `min-[860px]`) is already internally complementary,
            and moving either one to 861 would re-open the hole P8-H8 closed.
            THE PAN WRAPPER HOLDS ONLY THE TABLE. pm.css :56 makes `.tfoot` a
            CARD-level footer whose top border spans the card, and the design
            (projects-list.html :275) draws it as a sibling of `<table>` inside
            `.card` — there is no pan wrapper there at all, the pan is ours. So
            long as the footer lived INSIDE the scroller it was a block child of
            it and therefore only `clientWidth` wide: panned right, its top
            border stopped short of the panned view's right edge and "Showing N
            of M projects" walked off the left. The card element keeps
            `overflow-hidden` for its rounded-corner clip and the wrapper takes
            the pan, so the footer is once again the card's. Below 860 the
            wrapper carries NO overflow rule at all, so it cannot create a
            scroll context where the card deliberately dissolves. */}
        <div className="overflow-hidden rounded-2xl border border-border bg-card max-[860px]:rounded-none max-[860px]:border-0 max-[860px]:bg-transparent max-[860px]:overflow-visible">
          <div className="min-[860px]:overflow-x-auto">
            {visible.length === 0 ? (
              <EmptyState hasProjects={projects.length > 0} />
            ) : (
              <table className="w-full border-collapse max-[860px]:block">
                <thead className="max-[860px]:hidden">
                  <tr>
                    {COLUMNS.map((heading) => (
                      <th
                        key={heading}
                        className={cn(
                          "px-4 pt-[14px] pb-[13px] text-left text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-3",
                          BAND_DROPPED.includes(heading) && "max-[1199px]:hidden"
                        )}
                      >
                        {heading}
                      </th>
                    ))}
                    <th className="px-4 pt-[14px] pb-[13px] text-center text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink-3">
                      Requests
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody className="max-[860px]:block max-[860px]:w-full">
                  {visible.map((project) => (
                    <ProjectRow key={project.id} project={project} onReviewRequests={setReviewing} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {visible.length > 0 && (
            <div className="flex items-center justify-between border-t border-border px-[18px] py-[14px] text-[12px] font-semibold text-ink-3 max-[860px]:border-t-0 max-[860px]:px-0.5 max-[860px]:py-2">
              <span>
                Showing {visible.length} of {projects.length} project
                {projects.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
        </div>
      </div>
      <FreelancerRequestsDialog
        open={reviewing != null}
        title={reviewing?.title}
        requests={reviewing?.requestItems ?? []}
        onClose={() => setReviewing(null)}
      />
    </>
  )
}

Projects.layout = (page) => (
  <AppShell active="Projects" crumb={["Home", "Projects"]}>
    {page}
  </AppShell>
)
